import React, { useState } from "react";
import QRCode from "react-qr-code";
import Button from "src/components/Button";
import { colors } from "src/styles";
import styled from "styled-components";
import { Spinner } from "react-bootstrap";
import { generatePrintTemplate, PrintQrItem } from "./template";
import { Container } from "src/components/ListScreen";
import { DBLocation, ID } from "src/types";
import { IMAGES } from "src/assets";
import TemplateQuadrantSelectorModal from "src/components/atoms/TemplateModal/index";
// import { FirebaseDataService } from "src/services/Firebase/data";

type TimeAndAttendanceQRcodeParams = {
  clientId: ID;
  locationId: ID;
  location: DBLocation;
  printLocations?: Pick<DBLocation, "id" | "label">[];
  openQuadrantModalTrigger?: number;
  activeQuadrantLocation?: Pick<DBLocation, "id" | "label"> | null;
  locationQuadrantAssignments?: Record<string, number>;
  onAssignQuadrant?: (locationId: ID, quadrant: number) => void;
  onRequestQuadrantSelection?: (location: Pick<DBLocation, "id" | "label">) => void;
  onClearAllSelections?: () => void;
};

const TimeAndAttendanceQRcode = ({
  clientId,
  locationId,
  location,
  printLocations,
  openQuadrantModalTrigger = 0,
  activeQuadrantLocation = null,
  locationQuadrantAssignments = {},
  onAssignQuadrant,
  onRequestQuadrantSelection,
  onClearAllSelections,
}: TimeAndAttendanceQRcodeParams) => {
  const buildTimeAndAttendanceUrl = (targetLocationId: ID, timestamp: number) => {
    return window.location.origin + `/${clientId}/${targetLocationId}/time-and-attendance?timestamp=${timestamp}`;
  };

  const generateInitialUrl = () => {
    const initialTimestamp = Date.now();
    return buildTimeAndAttendanceUrl(locationId, initialTimestamp);
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>(() => generateInitialUrl());
  const [qrKey, setQrKey] = useState<number>(0);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState<boolean>(false);

  const handleGenerateNewQrcode = async () => {
    setIsLoading(true);

    try {
      const newTimestamp = Date.now();
      const newUrl = buildTimeAndAttendanceUrl(locationId, newTimestamp);

      // await FirebaseDataService.updateQRCodeByWashroomId(locationId as ID, washroomId as ID, newTimestamp.toString());

      setQrCodeUrl(newUrl);
      setQrKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error generating new QR code:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrintQrcode = () => {
    const selectedPrintLocations = printLocations && printLocations.length > 0 ? printLocations : [];

    const hasSelectedLocations = selectedPrintLocations.length > 0;

    if (!hasSelectedLocations) {
      alert("Please select at least one location.");
      return;
    }

    const firstLocationWithoutQuadrant = selectedPrintLocations.find((selectedLocation) => {
      const selectedLocationId = selectedLocation.id;

      if (!selectedLocationId) {
        return false;
      }

      const assignedQuadrant = locationQuadrantAssignments[selectedLocationId.toString()];
      return !(assignedQuadrant >= 1 && assignedQuadrant <= 4);
    });

    if (firstLocationWithoutQuadrant && onRequestQuadrantSelection) {
      onRequestQuadrantSelection(firstLocationWithoutQuadrant);
      setIsTemplateModalOpen(true);
      return;
    }

    const selectedLocationsOrdered = selectedPrintLocations.filter((selectedLocation) => Boolean(selectedLocation.id));

    const layoutLocations: Pick<DBLocation, "id" | "label">[] = new Array(4).fill(null);

    selectedLocationsOrdered.forEach((selectedLocation) => {
      const selectedLocationId = selectedLocation.id;

      if (!selectedLocationId) {
        return;
      }

      const assignedQuadrant = locationQuadrantAssignments[selectedLocationId.toString()];

      if (assignedQuadrant >= 1 && assignedQuadrant <= 4 && !layoutLocations[assignedQuadrant - 1]) {
        layoutLocations[assignedQuadrant - 1] = selectedLocation;
      }
    });

    let fallbackIndex = 0;

    for (let index = 0; index < layoutLocations.length; index += 1) {
      if (!layoutLocations[index]) {
        const fallbackLocation = selectedLocationsOrdered[fallbackIndex % selectedLocationsOrdered.length] || {
          id: locationId,
          label: location.label,
        };
        layoutLocations[index] = fallbackLocation;
        fallbackIndex += 1;
      }
    }

    const baseTimestamp = Date.now();
    const qrItems: PrintQrItem[] = layoutLocations.map((printLocation, index) => {
      const targetLocationId = printLocation.id || locationId;
      const locationLabel = printLocation.label || location.label;

      return {
        qrUrl: buildTimeAndAttendanceUrl(targetLocationId, baseTimestamp + index),
        locationLabel,
      };
    });

    const printContent = generatePrintTemplate(
      qrItems,
      IMAGES.COLLAPSED_LOGO_NAV_HIGH_RES,
      IMAGES.KSI_MERGEDLOGO,
      colors.kleenway.green,
    );
    const printWindow = window.open("", "_blank");

    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
    } else {
      console.error("Unable to open print window. Please check popup blocker settings.");
      alert("Unable to open print window. Please check your browser's popup blocker settings.");
    }
  };

  const handleQuadrantSelect = (quadrant: number) => {
    if (!activeQuadrantLocation?.id || !onAssignQuadrant) {
      return;
    }

    onAssignQuadrant(activeQuadrantLocation.id, quadrant);
  };

  const getDisabledQuadrants = () => {
    if (!activeQuadrantLocation?.id) {
      return [];
    }

    const activeLocationId = activeQuadrantLocation.id.toString();

    return Object.entries(locationQuadrantAssignments)
      .filter(([locationKey]) => locationKey !== activeLocationId)
      .map(([, assignedQuadrant]) => assignedQuadrant)
      .filter((assignedQuadrant) => assignedQuadrant >= 1 && assignedQuadrant <= 4);
  };

  const selectedQuadrantForActiveLocation =
    activeQuadrantLocation?.id && locationQuadrantAssignments[activeQuadrantLocation.id.toString()]
      ? locationQuadrantAssignments[activeQuadrantLocation.id.toString()]
      : null;

  React.useEffect(() => {
    if (openQuadrantModalTrigger > 0) {
      setIsTemplateModalOpen(true);
    }
  }, [openQuadrantModalTrigger]);

  return (
    <Container>
      <Content>
        <ContentFrame>
          <MainContainer>
            <QRContainer>
              {isLoading ? (
                <LoaderContainer>
                  <Spinner animation="border" size="sm" />
                  <LoaderText>Generating new QR code...</LoaderText>
                </LoaderContainer>
              ) : (
                <QRCode
                  key={qrKey} // Force re-render when key changes
                  size={36}
                  style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                  value={qrCodeUrl}
                  viewBox={`0 0 36 36`}
                />
              )}
            </QRContainer>

            <ButtonsContainer>
              <Button
                text={isLoading ? "Generating..." : "Generate New QR Code"}
                onClick={handleGenerateNewQrcode}
                disabled={isLoading}
                additionalStyles={{
                  color: colors.white.default,
                  padding: "12px 24px",
                  backgroundColor: isLoading ? colors.grey?.default || "#ccc" : colors.kleenway.green,
                  cursor: isLoading ? "not-allowed" : "pointer",
                }}
                variant="middle-primary"
              />
              <Button
                text="Print QR code"
                onClick={handlePrintQrcode}
                disabled={isLoading}
                additionalStyles={{
                  color: colors.white.default,
                  padding: "12px 24px",
                  backgroundColor: isLoading ? colors.grey?.default || "#ccc" : colors.kleenway.green,
                  cursor: isLoading ? "not-allowed" : "pointer",
                }}
                variant="middle-primary"
              />
            </ButtonsContainer>
          </MainContainer>
        </ContentFrame>
      </Content>

      <TemplateQuadrantSelectorModal
        isOpen={isTemplateModalOpen}
        locationName={activeQuadrantLocation?.label || location.label || "Selected Location"}
        onSelect={handleQuadrantSelect}
        onClose={() => setIsTemplateModalOpen(false)}
        appLogoSrc={IMAGES.KSI_MERGEDLOGO}
        collapsedLogoSrc={IMAGES.COLLAPSED_LOGO_NAV_HIGH_RES}
        primaryColor={colors.kleenway.green}
        selectedQuadrant={selectedQuadrantForActiveLocation}
        disabledQuadrants={getDisabledQuadrants()}
        onClearAllSelections={onClearAllSelections}
      />
    </Container>
  );
};

// Styled components remain the same
const Content = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 40px;
`;

const ContentFrame = styled.div`
  width: 70%;
`;

const MainContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 20px;
`;

const QRContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 200px;
  width: 30%;
`;

const LoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 10px;
`;

const LoaderText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #666;
`;

const ButtonsContainer = styled.div`
  width: auto;
  height: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export default TimeAndAttendanceQRcode;
